import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Button, Grid, TextField, Typography } from "@mui/material";
import validator from "validator";
import { SURFACE, TEXT } from "../../dark-theme/palette";
import { trackEvent } from "../../services/GoogleAnalytics";
import Logger from "../../services/Logger";
import UserLayoutService from "../route/UserLayoutService";
import { decryptText, mailingVerifyCodeMatches } from "../../services/CryptoUtil";

const siteName = process.env.REACT_APP_SITE_NAME || "Puzzle Interact";

function unsubscribeLinkValid(email, verifyCode) {
    const trimmed = (email || "").trim();
    if (!trimmed || !validator.isEmail(trimmed) || !verifyCode) return false;
    if (mailingVerifyCodeMatches(trimmed, verifyCode)) return true;
    const decoded = decryptText(verifyCode);
    return Boolean(decoded && decoded.trim().toLowerCase() === trimmed.toLowerCase());
}

function EmailPreferenceLayout({ title, children }) {
    return (
        <Box sx={{ flexGrow: 1, p: 3 }}>
            <Grid container spacing={4}>
                <Grid
                    size={{ xs: 12 }}
                    sx={{
                        color: TEXT.primary,
                        backgroundColor: SURFACE.card,
                        display: "flex",
                        justifyContent: "flex-start",
                        textAlign: "left",
                        p: 4,
                        borderRadius: 2,
                        boxShadow: 1,
                    }}
                >
                    <Box sx={{ width: "100%", maxWidth: 560 }}>
                        <Typography
                            variant="h3"
                            sx={{
                                color: "#3BA666",
                                fontSize: { xs: "32px", md: "48px" },
                                fontWeight: 600,
                                mb: 4,
                            }}
                        >
                            {title}
                        </Typography>
                        {children}
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}

function PreferenceForm({
    eventName,
    title,
    description,
    submitLabel,
    successTitle,
    successBody,
    extraSuccess,
    onSubmit,
    requireVerifyCode = false,
}) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const verifyCodeFromLink = (searchParams.get("verifyCode") || "").trim();
    const [email, setEmail] = useState(() => (searchParams.get("email") || "").trim());
    const linkOk = !requireVerifyCode || unsubscribeLinkValid(email, verifyCodeFromLink);
    const [errEmail, setErrEmail] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [done, setDone] = useState(false);

    useEffect(() => {
        trackEvent("entered", { name: eventName });
    }, [eventName]);

    useEffect(() => {
        if (!requireVerifyCode) return;
        if (!verifyCodeFromLink) {
            setError("Use the unsubscribe link from your email.");
            return;
        }
        if (!unsubscribeLinkValid(email, verifyCodeFromLink)) {
            setError("This unsubscribe link is invalid.");
        }
    }, [requireVerifyCode, verifyCodeFromLink, email]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        const trimmed = email.trim();
        if (!validator.isEmail(trimmed)) {
            setErrEmail(true);
            setError("Please enter a valid email address.");
            return;
        }
        if (requireVerifyCode && !unsubscribeLinkValid(trimmed, verifyCodeFromLink)) {
            setError(
                verifyCodeFromLink
                    ? "This unsubscribe link is invalid."
                    : "Use the unsubscribe link from your email."
            );
            return;
        }
        setErrEmail(false);
        setError("");
        setLoading(true);
        try {
            await onSubmit(trimmed, verifyCodeFromLink);
            setDone(true);
        } catch (err) {
            Logger.error(eventName, err);
            setError(typeof err === "string" ? err : "Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    if (done) {
        return (
            <EmailPreferenceLayout title={successTitle}>
                <Typography variant="body1" sx={{ color: TEXT.primary, fontSize: "18px", lineHeight: 1.8, mb: 2 }}>
                    {successBody}
                </Typography>
                {typeof extraSuccess === "function" ? extraSuccess(email) : extraSuccess}
                <Box sx={{ mt: 4, display: "flex", justifyContent: "center", gap: 2, flexWrap: "wrap" }}>
                    <Button
                        variant="contained"
                        onClick={() => navigate("/")}
                        sx={{
                            backgroundColor: "#3BA666",
                            color: "white",
                            textTransform: "none",
                            fontWeight: 600,
                            px: 4,
                            py: 1.5,
                            "&:hover": { backgroundColor: "#2e7d32" },
                        }}
                    >
                        Home
                    </Button>
                </Box>
            </EmailPreferenceLayout>
        );
    }

    return (
        <EmailPreferenceLayout title={title}>
            <Typography variant="body1" sx={{ color: TEXT.primary, fontSize: "18px", lineHeight: 1.8, mb: 3 }}>
                {description}
            </Typography>
            <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                    label="Email address"
                    value={email}
                    onChange={(e) => {
                        setEmail(e.target.value);
                        setErrEmail(false);
                    }}
                    error={errEmail}
                    helperText={
                        errEmail
                            ? "Please enter a valid email address."
                            : requireVerifyCode
                              ? "This address comes from your email link and cannot be changed."
                              : ""
                    }
                    fullWidth
                    type="email"
                    autoComplete="email"
                    disabled={requireVerifyCode}
                />
                {error ? (
                    <Typography variant="body2" sx={{ color: "error.main" }}>
                        {error}
                    </Typography>
                ) : null}
                <Button
                    type="submit"
                    variant="contained"
                    disabled={loading || (requireVerifyCode && !linkOk)}
                    sx={{
                        alignSelf: "flex-start",
                        backgroundColor: "#3BA666",
                        color: "white",
                        textTransform: "none",
                        fontWeight: 600,
                        px: 4,
                        py: 1.5,
                        "&:hover": { backgroundColor: "#2e7d32" },
                    }}
                >
                    {loading ? "Please wait…" : submitLabel}
                </Button>
            </Box>
        </EmailPreferenceLayout>
    );
}

export function EmailUnsubscribe() {
    const navigate = useNavigate();
    return (
        <PreferenceForm
            eventName="EmailUnsubscribe"
            title="Unsubscribe"
            description={`We'll stop sending ${siteName} emails to this address. Use the unsubscribe link from your email so this request can be verified.`}
            submitLabel="Unsubscribe"
            successTitle="You've been unsubscribed"
            successBody={`You will no longer receive emails from ${siteName} at this address.`}
            extraSuccess={(unsubscribedEmail) => (
                <Button
                    onClick={() => navigate(`/email/subscribe?email=${encodeURIComponent(unsubscribedEmail)}`)}
                    sx={{ textTransform: "none", color: "#3BA666", fontWeight: 600, px: 0 }}
                >
                    Subscribe again
                </Button>
            )}
            requireVerifyCode
            onSubmit={(email, verifyCode) => UserLayoutService.unsubscribeMailingList(email, verifyCode)}
        />
    );
}

export function EmailSubscribe() {
    return (
        <PreferenceForm
            eventName="EmailSubscribe"
            title="Subscribe"
            description={`Subscribe to ${siteName} emails, or subscribe again if you previously unsubscribed.`}
            submitLabel="Subscribe"
            successTitle="You're subscribed"
            successBody={`You'll receive emails from ${siteName} at this address. If this is a new subscription, please check your inbox to verify your email.`}
            onSubmit={(email) => UserLayoutService.postMailingList(email)}
        />
    );
}
